import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CloudUpload, Cloud, WifiOff, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { hasParticipated } from "@/lib/openPlayStore";

const KEY = (cid) => `pc-casual-sync:${cid}`;

function payload(state, clientId) {
  return {
    client_id: clientId,
    name: state.session?.eventName || "Open Play Session",
    location: state.session?.location || "",
    started_at: state.session?.startedAt ? new Date(state.session.startedAt).toISOString() : null,
    players: state.players.filter(hasParticipated).map((p) => ({ pid: p.id, name: p.name })),
    games: (state.history || []).map((h) => ({ team_a: h.team_a || [], team_b: h.team_b || [], score_a: h.score_a ?? null, score_b: h.score_b ?? null, winner: h.winner || null, playedAt: h.playedAt || null })),
  };
}

export default function CasualSync({ state, clientId }) {
  const nav = useNavigate();
  const [online, setOnline] = useState(navigator.onLine);
  const [info, setInfo] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY(clientId))) || null; } catch { return null; } });
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const sig = JSON.stringify([state.players.filter(hasParticipated).map((p) => p.name), (state.history || []).length, state.session?.eventName]);

  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const sync = async (manual) => {
    if (!navigator.onLine) { if (manual) toast.error("You're offline — connect to the internet to sync this session to PointControl."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/casual/sessions", payload(state, clientId));
      const next = { ...data, sig };
      localStorage.setItem(KEY(clientId), JSON.stringify(next));
      setInfo(next);
      if (manual) toast.success(`Published — ${data.matched}/${data.players} players linked to accounts`);
    } catch (e) { if (manual) toast.error(formatApiError(e.response?.data?.detail) || "Sync failed"); }
    setBusy(false);
  };

  // auto-sync (debounced) whenever players/games change and the device is online (first game auto-publishes)
  useEffect(() => {
    if (!online || (info && info.sig === sig) || (!info && !(state.history || []).length)) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => sync(false), 3000);
    return () => clearTimeout(timer.current);
  }, [sig, online, info]); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = info && info.sig !== sig;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="casual-sync">
      {!online && (info || state.history?.length) ? (
        <span className="rounded-full px-3 py-1.5 font-bold flex items-center gap-1.5" style={{ background: "rgba(245,158,11,0.15)", color: "#b45309" }} data-testid="casual-sync-offline">
          <WifiOff className="w-3.5 h-3.5" /> Offline — connect to the internet to sync to PointControl
        </span>
      ) : info ? (
        <span className="rounded-full px-3 py-1.5 font-bold flex items-center gap-1.5 glass" data-testid="casual-sync-status">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Cloud className="w-3.5 h-3.5 text-primary-c" />}
          {busy ? "Syncing…" : pending ? "Changes pending sync" : `Synced ${new Date(info.synced_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
        </span>
      ) : null}
      {!info && (
        <button data-testid="casual-publish-btn" onClick={() => sync(true)} disabled={busy} className="btn-glow rounded-full px-4 py-1.5 font-bold flex items-center gap-1.5">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloudUpload className="w-3.5 h-3.5" />} Publish to PointControl
        </button>
      )}
      {info && <button data-testid="casual-open-ratings-btn" onClick={() => nav(`/casual/${info.session_id}`)} className="rounded-full glass px-3 py-1.5 font-bold">Casual ratings</button>}
    </div>
  );
}
